import { tap, map, Observable } from 'rxjs';

import { inject, signal, Injectable } from '@angular/core';

import { ParentApiService } from './parent-api.service';

import { ParentRequestParams } from '@/app/entities';
import { Parent, UpdateParent } from '@/app/entities';

import { ApiResponseNonNull } from '@/app/shared/http';

@Injectable({
  providedIn: 'root',
})
export class ParentStore {
  private parentApiService = inject(ParentApiService);

  private _parents = signal<Parent[] | null>(null);
  readonly parents = this._parents.asReadonly();
  private _activeParent = signal<Parent | null>(null);
  readonly activeParent = this._activeParent.asReadonly();

  getParents(params: ParentRequestParams): Observable<ApiResponseNonNull<Parent[]>> {
    return this.parentApiService.getParents(params).pipe(tap((res) => this._parents.set(res.data)));
  }

  getParentById(id: number): Observable<Parent> {
    return this.parentApiService.getParentById(id).pipe(
      map((res) => res.data),
      tap((parent) => this._activeParent.set(parent)),
    );
  }

  updateParent(parent: UpdateParent, parentId: number): Observable<ApiResponseNonNull<Parent>> {
    return this.parentApiService.updateParent(parent, parentId).pipe(
      tap((res) => {
        const updatedParent = res.data;
        this._parents.update((parents) =>
          parents ? parents.map((p) => (p.id === updatedParent.id ? updatedParent : p)) : parents,
        );

        this._activeParent.update((activeParent) => this.updateActiveParentUsers(activeParent, updatedParent));
      }),
    );
  }

  private updateActiveParentUsers(activeParent: Parent | null, updatedParent: Parent): Parent | null {
    if (!activeParent) {
      return activeParent;
    }

    if (activeParent?.id === updatedParent.id) {
      return updatedParent;
    }

    return {
      ...activeParent,
      familyMembers: {
        ...activeParent.familyMembers,
        parentUsers: activeParent.familyMembers.parentUsers.map((user) =>
          user.id === updatedParent.id ? updatedParent : user,
        ),
      },
    };
  }

  deleteParent(id: number): Observable<void> {
    return this.parentApiService.deleteParent(id).pipe(
      tap(() => {
        this._parents.update((parents) => (parents ? parents.filter((p) => p.id !== id) : parents));
      }),
    );
  }
}
